import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';

import {
  adminExportDataSelector,
  adminExportMetaDataSelector,
  downloadAdminExportXlsx,
  getAdminExportData,
  isLoadingAdminExportSelector,
} from 'src/store/adminExport';
import { adminContactsSelector, getAdminContacts } from 'src/store/admin';

import { Button } from '../Button/Button';

export const useTable = () => {
  const dispatch = useDispatch();

  const adminContacts = useSelector(adminContactsSelector);
  const adminExportData = useSelector(adminExportDataSelector);
  const adminExportMetaData = useSelector(adminExportMetaDataSelector);
  const isLoading = useSelector(isLoadingAdminExportSelector);

  const [selectedCarrier, setSelectedCarrier] = useState(null);

  useEffect(() => {
    dispatch(getAdminContacts());
  }, [dispatch]);

  const isSelectedCarrier = !_.isEmpty(selectedCarrier);

  const onCLickExport = useCallback(async () => {
    dispatch(
      downloadAdminExportXlsx({
        issuer_id: selectedCarrier.id,
        display_name: selectedCarrier.display_name,
      }),
    );
  }, [dispatch, selectedCarrier]);

  const onChangeAutocomplete = useCallback((_, value) => {
    setSelectedCarrier(null);
    setSelectedCarrier(value);
  }, []);

  const getOptions = useMemo(
    () => (selectedCarrier?.id ? { issuer_id: selectedCarrier.id } : {}),
    [selectedCarrier?.id],
  );

  const api = useMemo(
    () => ({ get: selectedCarrier?.id ? getAdminExportData : null, get_options: getOptions }),
    [selectedCarrier?.id, getOptions],
  );

  const build = {
    title: 'Export',
    filters: [
      {
        id: 'carrier',
        label: 'Select Carrier',
        type: 'autocomplete',
        data: adminContacts || [],
        options: {
          minWidth: 300,
          value: selectedCarrier,
          onChange: onChangeAutocomplete,
          type: 'async',
          action: getAdminContacts,
          getOptionLabel: (option) => option?.label || '',
          isOptionEqualToValue: (option, value) => option?.id === value?.id,
        },
      },
      {
        id: 'q',
        label: 'Search',
        type: 'search-input',
      },
      {
        id: 'q2',
        component: Button,
        options: {
          onClick: onCLickExport,
          title: 'Export',
          type: 'primary',
          isDisabled: !isSelectedCarrier,
        },
      },
    ],
    columns: [
      {
        id: 'proposal_name',
        name: 'Proposal Name',
        sortable: true,
      },
      {
        id: 'company_name',
        name: 'Company Name',
        sortable: true,
      },
      {
        id: 'broker_name',
        name: 'Broker Name',
        sortable: true,
      },
      {
        id: 'coverage_type',
        name: 'Coverage Type',
        sortable: true,
      },
      {
        id: 'group_member_count',
        name: 'Group Members',
        sortable: false,
      },
      {
        id: 'quote_plan_count',
        name: 'Quote Plans',
        sortable: false,
      },
      {
        id: 'created_timestamp',
        name: 'Created Date',
        value: (item) => moment(item?.created_timestamp).format('l'),
        sortable: true,
      },
    ],
    data: isSelectedCarrier ? adminExportData : [],
    meta: adminExportMetaData,
    table_options: {
      empty_message: isSelectedCarrier
        ? 'No data found for this carrier.'
        : 'Please select a carrier.',
    },
    api,
    is_loading: isLoading,
  };

  return { build };
};
